import { createFileRoute } from "@tanstack/react-router";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HelpLink, HelpNote, HelpPage } from "@/components/HelpPage";

const faq: Array<{ q: string; a: React.ReactNode }> = [
  { q: "Livrez-vous partout au Maroc ?", a: <>Oui, SUNSET Concept Store livre dans toutes les villes du Maroc.</> },
  { q: "La livraison est-elle gratuite ?", a: <>Oui, la livraison est gratuite.</> },
  {
    q: "Quel est le délai de livraison ?",
    a: <>La livraison prend généralement entre 3 et 5 jours ouvrables après confirmation de la commande.</>,
  },
  { q: "Comment puis-je payer ma commande ?", a: <>Le paiement s'effectue à la livraison.</> },
  {
    q: "Ma commande doit-elle être confirmée ?",
    a: (
      <>
        Notre équipe peut vous contacter après votre commande afin de vérifier vos informations et votre adresse avant
        l'expédition.
      </>
    ),
  },
  {
    q: "Puis-je échanger un article ?",
    a: (
      <>
        Si vous souhaitez demander un échange, contactez notre équipe via la page{" "}
        <HelpLink to="/contact">Contact</HelpLink>. L'éligibilité dépend notamment de l'état de l'article et des
        conditions indiquées dans notre politique{" "}
        <HelpLink to="/echanges-retours">Échanges &amp; retours</HelpLink>.
      </>
    ),
  },
  {
    q: "Comment choisir ma taille ?",
    a: (
      <>
        Consultez notre <HelpLink to="/guide-des-tailles">Guide des tailles</HelpLink> ainsi que les informations
        disponibles sur la fiche du produit concerné.
      </>
    ),
  },
  {
    q: "Comment contacter SUNSET ?",
    a: (
      <>
        Vous pouvez nous écrire via notre page <HelpLink to="/contact">Contact</HelpLink>.
      </>
    ),
  },
];

const faqPlainAnswers: Record<string, string> = {
  "Livrez-vous partout au Maroc ?": "Oui, SUNSET Concept Store livre dans toutes les villes du Maroc.",
  "La livraison est-elle gratuite ?": "Oui, la livraison est gratuite.",
  "Quel est le délai de livraison ?":
    "La livraison prend généralement entre 3 et 5 jours ouvrables après confirmation de la commande.",
  "Comment puis-je payer ma commande ?": "Le paiement s'effectue à la livraison.",
  "Ma commande doit-elle être confirmée ?":
    "Notre équipe peut vous contacter après votre commande afin de vérifier vos informations et votre adresse avant l'expédition.",
  "Puis-je échanger un article ?":
    "Si vous souhaitez demander un échange, contactez notre équipe via la page Contact. L'éligibilité dépend notamment de l'état de l'article et des conditions indiquées dans notre politique Échanges & retours.",
  "Comment choisir ma taille ?":
    "Consultez notre Guide des tailles ainsi que les informations disponibles sur la fiche du produit concerné.",
  "Comment contacter SUNSET ?": "Vous pouvez nous écrire via notre page Contact, nous répondons sous 24 à 48 heures ouvrables.",
};

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: faqPlainAnswers[item.q] ?? "" },
  })),
};

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Questions fréquentes — Sunset Concept Store" },
      {
        name: "description",
        content:
          "Livraison, paiement à la livraison, échanges, tailles : les réponses aux questions les plus fréquentes de Sunset Concept Store.",
      },
      { property: "og:title", content: "Questions fréquentes — Sunset Concept Store" },
      {
        property: "og:description",
        content: "Toutes les réponses sur la livraison, le paiement, les échanges et les tailles.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sunsetconcept.store/faq" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sunsetconcept.store/faq" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd) }],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <HelpPage
      title="Questions fréquentes"
      intro="Retrouvez ici les réponses aux questions les plus souvent posées par nos clientes."
    >
      <Accordion type="single" collapsible className="w-full border-t border-border">
        {faq.map((item, i) => (
          <AccordionItem key={item.q} value={`q-${i}`} className="border-b border-border">
            <AccordionTrigger className="py-6 text-left font-serif text-lg font-light leading-snug tracking-tight hover:no-underline sm:text-xl">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="pb-6 text-sm leading-[1.9] text-muted-foreground sm:text-base">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <HelpNote>
        Vous ne trouvez pas votre réponse ? Consultez le{" "}
        <HelpLink to="/guide-des-tailles">Guide des tailles</HelpLink> ou écrivez-nous via la page{" "}
        <HelpLink to="/contact">Contact</HelpLink>.
      </HelpNote>
    </HelpPage>
  );
}
